import {
  Checkbox,
  Dialog,
  FormControl,
  FormControlLabel,
  FormHelperText,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  makeStyles,
  Radio,
  RadioGroup,
  Typography,
} from "@material-ui/core"
import clsx from "clsx"
import dayjs from "dayjs"
import * as EmailValidator from "email-validator"
import { useQueryClient } from "@tanstack/react-query"
import { FC, useEffect, useState } from "react"
import {
  AuthAccountsByEmailDocument,
  AuthAccountsByEmailQuery,
  AuthAccountsByEmailQueryVariables,
  GetPendingUserInviteDocument,
  GetPendingUserInviteQuery,
  GetPendingUserInviteQueryVariables,
  GetSuperviseesBySupervisorIdDocument,
  GetSuperviseesBySupervisorIdQuery,
  GetSuperviseesBySupervisorIdQueryVariables,
  InsertSupervisorDocument,
  InsertSupervisorMutation,
  InsertSupervisorMutationVariables,
  useDeleteOrganizationAdminMutation,
  useDeleteOrganizationMemberRoleMutation,
  useInsertOrganizationAdminMutation,
  useInsertOrgMemberRoleMutation,
  useInsertSuperviseesMutation,
  useInsertSupervisorMutation,
  useInsertUserInviteMutation,
  useUpdateAccountMutation,
  useUpdateAccountRoleMutation,
  useUpdatePractitionerMutation,
  useUpdateSupervisorEndDateMutation,
  useUpdateUserInviteMutation,
} from "../../generated/graphql"
import { useGraphQLClient } from "../../hooks/useGraphQLClient"
import { useOrganization } from "../../hooks/useOrganization"
import { useUser } from "../../hooks/useUser"
import { useApi } from "../../utils/API"
import { Query, Role } from "../../utils/constants"
import { colors } from "../../utils/theme"
import {
  GhostButton,
  PrimaryButton,
  SecondaryButton,
} from "../atoms/button/Button"
import CloseIcon from "../atoms/CloseIcon"
import Input from "../atoms/input/Input"

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    border: `1px solid ${colors.neutral.clay}`,
    boxShadow: "none",
  },
  dialogCloseContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    marginBottom: theme.spacing(1),
  },
  dialogContent: {
    padding: theme.spacing(5),
    paddingTop: theme.spacing(2),
  },
  closeButton: {
    margin: 0,
    padding: 0,
    border: 0,
    height: theme.spacing(2),
    width: theme.spacing(2),
    backgroundColor: "transparent",
    cursor: "pointer",
    "&:focus": {
      outline: "none",
    },
  },
  dialogTitle: {
    color: colors.neutral.denim,
    marginBottom: 12,
  },
  dialogSubtitle: {
    color: colors.neutral.denim,
    marginBottom: theme.spacing(3),
  },
  dialogInput: {
    width: "100%",
    marginBottom: theme.spacing(3),
  },
  dialogButton: {
    width: "100%",
  },
  cancelContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginTop: theme.spacing(2),
  },
  changingRoleText: {
    color: colors.neutral.denim,
    textAlign: "center",
  },
  changingRoleContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: 64,
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
    backgroundColor: colors.secondary.blush,
    borderRadius: 4,
    marginBottom: theme.spacing(3),
  },
  editTitle: {
    marginBottom: theme.spacing(3),
  },
  dialogButtonSupervisor: {
    width: "100%",
    marginBottom: theme.spacing(2),
  },
  dialogHeader: {
    textAlign: "center",
    margin: theme.spacing(3),
  },
}))

const AddStaff: FC<{
  onClose: () => void
  onSuccess: () => void
  firstName: string
  setFirstName: (value: string) => void
  lastName: string
  setLastName: (value: string) => void
  email: string
  setEmail: (value: string) => void
  role?: Role
  setRole: (value?: Role) => void
  isEdit?: boolean
  isPending?: boolean
  id?: number | string
  initialRole?: Role
}> = ({
  onClose,
  onSuccess,
  firstName,
  setFirstName,
  lastName,
  setLastName,
  email,
  setEmail,
  role,
  setRole,
  isEdit,
  isPending,
  id,
  initialRole,
}) => {
  const classes = useStyles()
  const { user } = useUser()
  const graphqlClient = useGraphQLClient()
  const queryClient = useQueryClient()
  const { mutate: insertUserInvite } = useInsertUserInviteMutation(
    graphqlClient,
    {
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: [Query.GET_ORGANIZATION] }),
    }
  )
  const [firstNameError, setFirstNameError] = useState<string | undefined>(
    undefined
  )
  const [lastNameError, setLastNameError] = useState<string | undefined>(
    undefined
  )
  const [emailError, setEmailError] = useState<string | undefined>(undefined)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [roleError, setRoleError] = useState<string | undefined>(undefined)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [changedRole, setChangedRole] = useState<boolean>(false)
  const API = useApi()
  const { mutate: updateUserInvite } = useUpdateUserInviteMutation(
    graphqlClient,
    {
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: [Query.GET_ORGANIZATION] }),
    }
  )
  const { mutate: updatePractitioner } = useUpdatePractitionerMutation(
    graphqlClient,
    {
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: [Query.GET_ORGANIZATION] }),
    }
  )
  const { mutate: updateAccount } = useUpdateAccountMutation(graphqlClient, {
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [Query.GET_ORGANIZATION] }),
  })
  const { mutate: deleteOrganizationAdmin } =
    useDeleteOrganizationAdminMutation(graphqlClient)
  const { mutate: insertOrganizationAdmin } =
    useInsertOrganizationAdminMutation(graphqlClient)

  const { mutate: insertOrgnizationMemberRole } =
    useInsertOrgMemberRoleMutation(graphqlClient, {
      onSuccess: () =>
        queryClient.invalidateQueries({
          queryKey: [Query.GET_SUPERVISORS_BY_ORG_ID],
        }),
    })

  const { mutate: insertSupervisor } = useInsertSupervisorMutation(
    graphqlClient,
    {
      onSuccess: () =>
        queryClient.invalidateQueries({
          queryKey: [Query.GET_SUPERVISORS_BY_ORG_ID],
        }),
    }
  )

  const { mutate: deleteOrgMemberRole } =
    useDeleteOrganizationMemberRoleMutation(graphqlClient)

  const { mutate: updateSupervisorEndDate } =
    useUpdateSupervisorEndDateMutation(graphqlClient, {
      onSuccess: () =>
        queryClient.invalidateQueries({
          queryKey: [Query.GET_SUPERVISORS_BY_ORG_ID],
        }),
    })

  const { mutate: updateAccountRole } = useUpdateAccountRoleMutation(
    graphqlClient,
    {
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: [Query.GET_ORGANIZATION] }),
    }
  )

  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false)

  const close = () => {
    setFirstNameError(undefined)
    setLastNameError(undefined)
    setEmailError(undefined)
    setRoleError(undefined)
    setChangedRole(false)
    onClose()
  }

  return (
    <div className={classes.dialogContent}>
      <div className={classes.dialogCloseContainer}>
        <button className={classes.closeButton} onClick={close}>
          <CloseIcon />
        </button>
      </div>
      <Typography
        variant="h3"
        className={clsx(
          classes.dialogTitle,
          isPending ? classes.editTitle : undefined
        )}
      >
        {isEdit ? "Edit Staff Member" : "Add New Staff Member"}
      </Typography>
      {!isEdit && (
        <>
          <Typography variant="body1" className={classes.dialogSubtitle}>
            Invite a new staff member by entering their email below.
          </Typography>
          <Typography variant="body1" className={classes.dialogSubtitle}>
            Once they confirm via email, you will be able to access their data
            via the Staff table.
          </Typography>
        </>
      )}
      <Input
        label="First Name"
        placeholder="First Name"
        value={firstName}
        onChange={(event) => setFirstName(event.target.value)}
        className={classes.dialogInput}
        error={Boolean(firstNameError)}
        helperText={firstNameError}
      />
      <Input
        label="Last Name"
        placeholder="Last Name"
        value={lastName}
        onChange={(event) => setLastName(event.target.value)}
        className={classes.dialogInput}
        error={Boolean(lastNameError)}
        helperText={lastNameError}
      />
      <Input
        label="Email"
        placeholder="Email"
        value={email}
        onChange={(event) => setEmail(event.target.value)}
        className={classes.dialogInput}
        error={Boolean(emailError)}
        helperText={emailError}
      />
      <Typography variant="h4">Staff Member Role</Typography>
      <FormControl error={Boolean(roleError)}>
        <RadioGroup
          aria-label="role"
          name="role"
          value={role}
          onChange={(event) => {
            setRole(event.target.value as Role)
            if (isEdit) {
              setChangedRole(true)
            }
          }}
          className={classes.dialogInput}
        >
          <FormControlLabel
            value={Role.ADMIN}
            control={<Radio />}
            label="Administrator"
          />
          <FormControlLabel
            value={Role.PRACTITIONER}
            control={<Radio />}
            label="Practitioner"
          />
          <FormControlLabel
            value={Role.WEB_ROLE}
            control={<Radio />}
            label="Supervisor"
          />
          <FormHelperText>{roleError}</FormHelperText>
        </RadioGroup>
      </FormControl>
      {changedRole && (
        <div className={classes.changingRoleContainer}>
          <Typography variant="body1" className={classes.changingRoleText}>
            {`Changing ${firstName} ${lastName}’s role will affect their permissions.`}
          </Typography>
        </div>
      )}
      {role === Role.WEB_ROLE && isEdit && (
        <>
          <PrimaryButton
            className={classes.dialogButtonSupervisor}
            onClick={() => setOpenEditDialog(true)}
          >
            Edit Supervisees
          </PrimaryButton>
          <AddSuperviseeDialog
            open={openEditDialog}
            onClose={() => setOpenEditDialog(false)}
            id={id}
          />
        </>
      )}
      <PrimaryButton
        onClick={async () => {
          setFirstNameError(undefined)
          setLastNameError(undefined)
          setEmailError(undefined)
          // setRoleError(undefined)
          let errors = false
          if (firstName === "") {
            setFirstNameError("You need to enter a first name to continue.")
            errors = true
          }
          if (lastName === "") {
            setLastNameError("You need to enter a last name to continue.")
            errors = true
          }
          if (email === "" || !EmailValidator.validate(email)) {
            setEmailError("You need to enter a valid email to continue.")
            errors = true
          }
          if (!role) {
            setRoleError(
              "You must assign a role to the staff member to continue."
            )
            errors = true
          }
          if (errors) {
            return
          }
          const [checkUserResponse, checkInviteResponse] = await Promise.all([
            graphqlClient.request<
              AuthAccountsByEmailQuery,
              AuthAccountsByEmailQueryVariables
            >(AuthAccountsByEmailDocument, {
              email,
            }),
            graphqlClient.request<
              GetPendingUserInviteQuery,
              GetPendingUserInviteQueryVariables
            >(GetPendingUserInviteDocument, {
              email,
            }),
          ])
          if (!isEdit) {
            if (checkInviteResponse.user_invites.length > 0) {
              setEmailError(
                "A pending user with this email address already exists."
              )
              return
            }
            if (checkUserResponse.auth_accounts.length > 0) {
              setEmailError(
                "An account with this email address already exists."
              )
              return
            }
            insertUserInvite({
              first_name: firstName,
              last_name: lastName,
              email,
              role: role,
              status: "pending",
              organization_id: user?.organizations?.[0].id ?? -1,
            })
            API.emailStaffInvite(email, firstName, lastName)
            onSuccess()
          } else {
            // is an edit
            const existingEmailsNotCurrentUser = [
              ...checkUserResponse.auth_accounts.map((account) => ({
                id: account.user_id,
              })),
              ...checkInviteResponse.user_invites,
            ].filter((user) => user.id !== id)
            if (existingEmailsNotCurrentUser.length > 0) {
              setEmailError(
                "An account with this email address already exists."
              )
              return
            }
            if (isPending) {
              updateUserInvite({
                id: (id as number) ?? -1,
                first_name: firstName,
                last_name: lastName,
                role: role,
                email,
              })
            } else {
              updatePractitioner({
                id,
                display_name: email,
                first_name: firstName,
                last_name: lastName,
              })
              updateAccount({
                userId: id,
                email,
              })

              if (role !== initialRole) {
                if (initialRole === Role.PRACTITIONER) {
                  // if the initial role was practitioner

                  if (role === Role.WEB_ROLE) {
                    // if the new role is supervisor
                    insertOrgnizationMemberRole({
                      practitioner_id: id,
                      org_id: user?.organizations?.[0].id ?? -1,
                      role: Role.WEB_ROLE,
                      type: "supervisor",
                    })

                    updateAccountRole({
                      userId: id,
                      role: Role.WEB_ROLE,
                    })

                    // check if the practitioner has an existing organization_supervisor record
                    // if not, create one
                    const response = await graphqlClient.request<
                      GetSuperviseesBySupervisorIdQuery,
                      GetSuperviseesBySupervisorIdQueryVariables
                    >(GetSuperviseesBySupervisorIdDocument, {
                      userId: id,
                    })
                    const supervisors = response.organization_supervisors

                    if (supervisors.length === 0) {
                      insertSupervisor({
                        supervisor: [
                          {
                            supervisor_id: id,
                            org_id: user?.organizations?.[0].id ?? -1,
                            ended_at: null,
                            started_at: dayjs.utc(),
                          },
                        ],
                      })
                    }
                  } else {
                    // if the new role is admin
                    insertOrganizationAdmin({
                      practitioner_id: id,
                      organization_id: user?.organizations?.[0].id ?? -1,
                    })
                  }
                } else if (initialRole === Role.WEB_ROLE) {
                  // if the initial role was supervisor

                  if (role === Role.ADMIN) {
                    // if the new role is admin
                    insertOrganizationAdmin({
                      practitioner_id: id,
                      organization_id: user?.organizations?.[0].id ?? -1,
                    })
                  }

                  updateAccountRole({
                    userId: id,
                    role: Role.PRACTITIONER,
                  })
                  deleteOrgMemberRole({
                    userId: id,
                  })
                  updateSupervisorEndDate({
                    userId: id,
                    orgId: user?.organizations?.[0].id ?? -1,
                    endDate: dayjs.utc(),
                  })
                } else {
                  // if the initial role was admin

                  if (role === Role.WEB_ROLE) {
                    // if the new role is supervisor
                    insertOrgnizationMemberRole({
                      practitioner_id: id,
                      org_id: user?.organizations?.[0].id ?? -1,
                      role: Role.WEB_ROLE,
                      type: "supervisor",
                    })

                    // check if the practitioner has an existing organization_supervisor record
                    // if not, create one
                    const response = await graphqlClient.request<
                      GetSuperviseesBySupervisorIdQuery,
                      GetSuperviseesBySupervisorIdQueryVariables
                    >(GetSuperviseesBySupervisorIdDocument, {
                      userId: id,
                    })
                    const supervisors = response.organization_supervisors

                    if (supervisors.length === 0) {
                      insertSupervisor({
                        supervisor: [
                          {
                            supervisor_id: id,
                            org_id: user?.organizations?.[0].id ?? -1,
                            ended_at: null,
                            started_at: dayjs.utc(),
                          },
                        ],
                      })
                    }
                  }

                  deleteOrganizationAdmin({
                    practitioner_id: id,
                    organization_id: user?.organizations?.[0].id ?? -1,
                  })
                }
              }
              setChangedRole(false)
            }
            close()
          }
        }}
        className={classes.dialogButton}
        type="submit"
      >
        {isEdit ? "Save Changes" : "Add Staff Member"}
      </PrimaryButton>
      {isEdit && (
        <div className={classes.cancelContainer}>
          <GhostButton onClick={onClose}>Cancel</GhostButton>
        </div>
      )}
    </div>
  )
}

const Success: FC<{ onClose: () => void; name: string }> = ({
  onClose,
  name,
}) => {
  const classes = useStyles()
  return (
    <div className={classes.dialogContent}>
      <div className={classes.dialogCloseContainer}>
        <button className={classes.closeButton} onClick={onClose}>
          <CloseIcon />
        </button>
      </div>
      <Typography variant="h2" className={classes.dialogTitle}>
        Staff Member Successfully Added
      </Typography>
      <Typography variant="body1" className={classes.dialogSubtitle}>
        {`${name} has been successfully added to your dashboard. ` +
          "Once they confirm via email, you will be able to access their data."}
      </Typography>
      <PrimaryButton
        onClick={onClose}
        className={classes.dialogButton}
        type="submit"
      >
        Close
      </PrimaryButton>
    </div>
  )
}

const AddStaffDialog: FC<{
  open: boolean
  onClose: () => void
  isEdit?: boolean
  isPending?: boolean
  id?: number | string
  currentFirstName?: string
  currentLastName?: string
  currentEmail?: string
  currentRole?: Role
}> = ({
  open,
  onClose,
  isEdit,
  isPending,
  id,
  currentFirstName,
  currentLastName,
  currentEmail,
  currentRole,
}) => {
  const classes = useStyles()
  const [submitted, setSubmitted] = useState<boolean>(false)
  const [firstName, setFirstName] = useState<string>(currentFirstName ?? "")
  const [lastName, setLastName] = useState<string>(currentLastName ?? "")
  const [email, setEmail] = useState<string>(currentEmail ?? "")
  const [role, setRole] = useState<Role | undefined>(currentRole ?? Role.ADMIN)

  const close = () => {
    onClose()
    setSubmitted(false)
    if (!isEdit) {
      setFirstName("")
      setLastName("")
      setEmail("")
      setRole(undefined)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={close}
      fullWidth={true}
      maxWidth="xs"
      classes={{
        paper: classes.dialogPaper,
      }}
    >
      {!submitted ? (
        <AddStaff
          onClose={close}
          onSuccess={() => setSubmitted(true)}
          firstName={firstName}
          setFirstName={setFirstName}
          lastName={lastName}
          setLastName={setLastName}
          email={email}
          setEmail={setEmail}
          role={role}
          setRole={setRole}
          isEdit={isEdit}
          isPending={isPending}
          id={id}
          initialRole={currentRole}
        />
      ) : (
        <Success onClose={close} name={`${firstName} ${lastName}`} />
      )}
    </Dialog>
  )
}

const AddSuperviseeDialog: FC<{
  open: boolean
  onClose: () => void
  id?: number | string
}> = ({
  open,
  onClose,
  id,
}: {
  open: boolean
  onClose: () => void
  id?: number | string
}) => {
  const classes = useStyles()
  const [submitted, setSubmitted] = useState<boolean>(false)
  const { staffTableData } = useOrganization()
  const [checked, setChecked] = useState<string[]>([]) // State to manage checked items

  const [unchecked, setUnchecked] = useState<string[]>([])
  const [origSelected, setOrigSelected] = useState<string[]>([])
  const [startedAt, setStartedAt] = useState<
    { entry: string; started_at: any }[]
  >([])

  const [orgSupervisorId, setOrgSupervisorId] = useState<string | undefined>("")

  const [isSupervisor, setIsSupervisor] = useState<boolean>(false)

  const { user } = useUser()

  const graphqlClient = useGraphQLClient()
  const queryClient = useQueryClient()

  const { mutate: insertSupervisees } = useInsertSuperviseesMutation(
    graphqlClient,
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [Query.GET_SUPERVISORS_BY_ORG_ID],
        })
        setSubmitted(true)
      },
    }
  )

  const organizationId = user?.organizations?.[0].id ?? -1

  useEffect(() => {
    const fetchedSupervisees = async () => {
      try {
        const response = await graphqlClient.request<
          GetSuperviseesBySupervisorIdQuery,
          GetSuperviseesBySupervisorIdQueryVariables
        >(GetSuperviseesBySupervisorIdDocument, {
          userId: id,
        })
        const supervisors = response.organization_supervisors

        let ids: string[] = []
        let startedAtArray: { entry: string; started_at: any }[] = []
        if (supervisors.length > 0) {
          setIsSupervisor(true)
          setOrgSupervisorId(supervisors[0].id)
        }

        for (let supervisor of supervisors ?? []) {
          // Add each supervisee's ID to the checked state
          const { organization_supervisors_organization_supervisees } =
            supervisor
          for (let entry of organization_supervisors_organization_supervisees ??
            []) {
            ids.push(entry.supervisees?.id ?? "")
            startedAtArray.push({
              entry: entry.supervisees?.id ?? "",
              started_at: entry.started_at,
            })
          }
        }

        setChecked(ids)
        setOrigSelected(ids)
        setStartedAt(startedAtArray)
      } catch (error) {}
    }
    fetchedSupervisees()
  }, [id])

  const close = () => {
    onClose()
  }

  const handleSave = async () => {
    const allSupervisees = [...checked, ...unchecked]

    const supervisees = []

    if (!isSupervisor) {
      const insertSupervisorMutation = await graphqlClient.request<
        InsertSupervisorMutation,
        InsertSupervisorMutationVariables
      >(InsertSupervisorDocument, {
        supervisor: [
          {
            supervisor_id: id,
            org_id: organizationId,
            started_at: dayjs.utc(),
            ended_at: null,
          },
        ],
      })

      for (let supervisee of allSupervisees) {
        const staffMember = staffTableData?.find(
          (staff) => staff.id.data === supervisee
        )
        if (
          staffMember &&
          !origSelected.includes(supervisee) &&
          checked.includes(supervisee)
        ) {
          supervisees.push({
            practitioner_id: staffMember.id.data,
            organization_supervisor_id:
              insertSupervisorMutation.insert_organization_supervisors
                ?.returning[0].id,
            started_at: dayjs.utc(),
            ended_at: null,
          })
        } else if (
          staffMember &&
          origSelected.includes(supervisee) &&
          unchecked.includes(supervisee)
        ) {
          // if the supervisee was removed, set their end date to now
          const startedAtEntry = startedAt.find(
            (entry) => entry.entry === supervisee
          )
          supervisees.push({
            practitioner_id: staffMember?.id.data,
            organization_supervisor_id:
              insertSupervisorMutation.insert_organization_supervisors
                ?.returning[0].id,
            started_at: startedAtEntry?.started_at,
            ended_at: dayjs.utc(),
          })
        }
      }

      if (supervisees.length > 0) {
        insertSupervisees({
          supervisees,
        })
      }
    } else {
      for (let supervisee of allSupervisees) {
        const staffMember = staffTableData?.find(
          (staff) => staff.id.data === supervisee
        )
        if (
          staffMember &&
          !origSelected.includes(supervisee) &&
          checked.includes(supervisee)
        ) {
          supervisees.push({
            practitioner_id: staffMember.id.data,
            organization_supervisor_id: orgSupervisorId,
            started_at: dayjs.utc(),
            ended_at: null,
          })
        } else if (
          staffMember &&
          origSelected.includes(supervisee) &&
          unchecked.includes(supervisee)
        ) {
          // if the supervisee was removed, set their end date to now
          const startedAtEntry = startedAt.find(
            (entry) => entry.entry === supervisee
          )
          supervisees.push({
            practitioner_id: staffMember?.id.data,
            organization_supervisor_id: orgSupervisorId,
            ended_at: dayjs.utc(),
            started_at: startedAtEntry?.started_at,
          })
        }
      }

      if (supervisees.length > 0) {
        insertSupervisees({
          supervisees,
        })
      }
    }

    setSubmitted(true)
  }

  const handleToggle = (value: any) => () => {
    const currentIndex = checked.indexOf(value)
    const newChecked = [...checked]

    if (currentIndex === -1) {
      // adding a new supervisee
      newChecked.push(value)
    } else {
      newChecked.splice(currentIndex, 1) // removing an existing supervisee
      setUnchecked([value])
    }

    setChecked(newChecked)
  }

  const filteredStaff = staffTableData?.filter((staff) => {
    return (
      ![user?.id, id].includes(staff.id.data) &&
      staff.more?.data?.pending !== true
    )
  })
  return (
    <Dialog
      open={open}
      onClose={close}
      fullWidth={true}
      maxWidth="xs"
      classes={{
        paper: classes.dialogPaper,
      }}
    >
      <div className={classes.dialogHeader}>
        <Typography variant="h2" className={clsx(classes.dialogTitle)}>
          Supervisees
        </Typography>
      </div>
      <List>
        {filteredStaff &&
          filteredStaff.map((value) => {
            const labelId = `checkbox-list-label-${value.id.data}`

            return (
              <ListItem
                key={value.id.data}
                dense
                button
                onClick={handleToggle(value.id.data)}
                //disabled={checked.includes(value.id.data)}
              >
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    checked={checked.includes(value.id.data)}
                    tabIndex={-1}
                    disableRipple
                  />
                </ListItemIcon>
                <ListItemText
                  id={labelId}
                  primary={`${value.firstName.data} ${value.lastName.data}`}
                />
              </ListItem>
            )
          })}
      </List>
      <SecondaryButton
        onClick={() => {
          handleSave()
          close()
        }}
        className={classes.dialogButtonSupervisor}
        type="submit"
      >
        {submitted ? "Saved" : "Save"}
      </SecondaryButton>
    </Dialog>
  )
}

export default AddStaffDialog
